import { ReputationTooltipContent } from '@/components/shared/reputationTooltipContent'
import type { Vault } from '@/types/vault'
import { cn } from '@/utils/cn'
import { getScoreRange, getScoreValue } from '@/utils/getScoreRangeValue'
import { NOT_RATED_LABEL } from '@/utils/reputationRating'
import { MAX_SCORE, getScoreSvgColors, getScoreTextClass } from '@/utils/scoreColors'
import { useId } from 'react'
import { Tooltip, TooltipContent } from '../common/tooltip'

interface Props {
  score: NonNullable<Vault['score']>
  isNotRated?: boolean
}

export function ReputationChart({ score, isNotRated = false }: Props) {
  const { vaultScore, totalScorePenalty } = score
  const id = useId()
  const gradientId = `rep-gradient${id}`
  const penaltyGradientId = `rep-penalty-gradient${id}`
  const scoreSweepId = `rep-score-sweep${id}`
  const penaltySweepId = `rep-penalty-sweep${id}`

  const range = getScoreRange(vaultScore)
  const { inner, outer } = getScoreSvgColors(range)
  const textColor = isNotRated ? 'text-heading-foreground' : getScoreTextClass(range)
  const scoreSweepPath = isNotRated ? '' : buildSweepPath(getSweepClipAngle(vaultScore), 50, 50, 55)
  const penaltySweepPath =
    !isNotRated && totalScorePenalty > 0
      ? buildSweepPath(getSweepClipAngle(vaultScore + totalScorePenalty), 50, 50, 55)
      : null

  return (
    <div className="group relative flex flex-col items-center justify-center">
      <svg viewBox="0 0 100 50" fill="none" className="w-full max-w-52">
        <defs>
          <radialGradient id={gradientId} cx="50" cy="50" r="55" gradientUnits="userSpaceOnUse">
            <stop offset="0%" stopColor={inner} />
            <stop offset="100%" stopColor={outer} />
          </radialGradient>
          <radialGradient id={penaltyGradientId} cx="50" cy="50" r="55" gradientUnits="userSpaceOnUse">
            <stop offset="0%" stopColor="rgb(var(--disabled))" stopOpacity={1} />
            <stop offset="100%" stopColor="rgb(var(--disabled))" stopOpacity={0.25} />
          </radialGradient>
          {scoreSweepPath && (
            <clipPath id={scoreSweepId}>
              <path d={scoreSweepPath} />
            </clipPath>
          )}
          {penaltySweepPath && (
            <clipPath id={penaltySweepId}>
              <path d={penaltySweepPath} />
            </clipPath>
          )}
        </defs>
        <g>
          {SEGMENT_PATHS.map((d, i) => (
            <path
              key={i}
              d={d}
              strokeWidth={1}
              className="fill-border stroke-border transition-all group-hocus:stroke-heading-foreground"
            />
          ))}
          {penaltySweepPath && (
            <g clipPath={`url(#${penaltySweepId})`}>
              {SEGMENT_PATHS.map((d, i) => (
                <path
                  key={i}
                  d={d}
                  fill={`url(#${penaltyGradientId})`}
                  strokeWidth={1}
                  className="stroke-border transition-all group-hocus:stroke-heading-foreground"
                />
              ))}
            </g>
          )}
          {scoreSweepPath && (
            <g clipPath={`url(#${scoreSweepId})`}>
              {SEGMENT_PATHS.map((d, i) => (
                <path
                  key={i}
                  d={d}
                  fill={`url(#${gradientId})`}
                  strokeWidth={1}
                  className="stroke-border transition-all group-hocus:stroke-heading-foreground"
                />
              ))}
            </g>
          )}
        </g>
      </svg>
      <Tooltip className={cn('group absolute flex h-full w-full items-center justify-center rounded-xl')}>
        <div className="absolute bottom-2">
          <p
            className={cn(
              'font-medium text-heading-foreground underline decoration-border underline-offset-4 transition-colors group-hocus:decoration-heading-foreground',
              isNotRated ? 'text-lg' : 'text-2xl'
            )}
          >
            {isNotRated ? (
              NOT_RATED_LABEL
            ) : (
              <>
                <span className={cn(textColor)}>{getScoreValue(vaultScore)}</span> / 10
              </>
            )}
          </p>
        </div>

        <TooltipContent>
          <ReputationTooltipContent
            score={vaultScore}
            scoreRange={isNotRated ? undefined : range}
            isNotRated={isNotRated}
            totalScorePenalty={totalScorePenalty}
          />
        </TooltipContent>
      </Tooltip>
    </div>
  )
}

const NUM_SEGMENTS = 10

const SEGMENT_PATHS = [
  'M7.65484 48.9544C4.53176 48.9544 1.96563 46.414 2.33271 43.3126C2.69979 40.2112 5.78804 38.34 8.82483 39.0691L16.0879 40.8128C18.0618 41.2867 19.2548 43.2713 19.0162 45.2872C18.7776 47.3031 17.1543 48.9544 15.1243 48.9544H7.65484Z',
  'M9.84308 35.5183C6.88139 34.5274 5.25394 31.3041 6.58615 28.4794C7.91836 25.6547 11.4407 24.8602 14.0893 26.5152L20.4237 30.4734C22.1453 31.5491 22.6469 33.8097 21.781 35.6457C20.915 37.4817 18.8517 38.5326 16.9266 37.8884L9.84308 35.5183Z',
  'M16.1802 23.47C13.686 21.5905 13.1654 18.0174 15.3251 15.7614C17.4847 13.5054 21.0772 13.8696 23.0637 16.2794L27.8149 22.0431C29.1061 23.6095 28.8645 25.9123 27.4608 27.3787C26.057 28.8451 23.7668 29.1869 22.1456 27.9653L16.1802 23.47Z',
  'M26.0154 14.0567C24.2465 11.4829 24.8866 7.92918 27.6505 6.47502C30.4144 5.02087 33.7056 6.50618 34.8248 9.42183L37.5016 16.3952C38.2291 18.2903 37.2693 20.3976 35.4728 21.3428C33.6763 22.288 31.396 21.8854 30.2462 20.2125L26.0154 14.0567Z',
  'M38.3282 8.24953C37.4674 5.24743 39.202 2.0805 42.2845 1.57849C45.3669 1.07648 48.0168 3.52935 48.153 6.64946L48.4788 14.1118C48.5674 16.1399 46.9885 17.8337 44.9849 18.16C42.9813 18.4863 40.9466 17.381 40.3871 15.4296L38.3282 8.24953Z',
  'M51.8455 6.64946C51.9817 3.52935 54.6316 1.07648 57.7141 1.57849C60.7965 2.0805 62.5312 5.24743 61.6703 8.24953L59.6115 15.4296C59.0519 17.381 57.0172 18.4863 55.0136 18.16C53.01 17.8337 51.4312 16.1399 51.5197 14.1118L51.8455 6.64946Z',
  'M65.1737 9.42182C66.2929 6.50618 69.5842 5.02087 72.3481 6.47502C75.112 7.92918 75.7521 11.4829 73.9831 14.0567L69.7524 20.2125C68.6026 21.8854 66.3223 22.288 64.5258 21.3428C62.7292 20.3976 61.7694 18.2903 62.4969 16.3952L65.1737 9.42182Z',
  'M76.9329 16.2794C78.9194 13.8696 82.5119 13.5054 84.6715 15.7614C86.8312 18.0174 86.3106 21.5905 83.8164 23.47L77.851 27.9653C76.2298 29.1869 73.9396 28.8451 72.5359 27.3787C71.1321 25.9123 70.8905 23.6095 72.1817 22.0431L76.9329 16.2794Z',
  'M85.9125 26.5152C88.5611 24.8602 92.0834 25.6547 93.4156 28.4794C94.7478 31.3041 93.1204 34.5274 90.1587 35.5183L83.0752 37.8884C81.1501 38.5326 79.0868 37.4817 78.2208 35.6457C77.3549 33.8097 77.8565 31.5491 79.5781 30.4734L85.9125 26.5152Z',
  'M91.177 39.0691C94.2138 38.34 97.3021 40.2112 97.6691 43.3126C98.0362 46.414 95.4701 48.9544 92.347 48.9544H84.8775C82.8475 48.9544 81.2242 47.3031 80.9856 45.2872C80.747 43.2713 81.94 41.2867 83.9139 40.8128L91.177 39.0691Z',
]

function segmentCenterAngle(i: number) {
  return 180 - (i / (NUM_SEGMENTS - 1)) * 180
}

function getSweepClipAngle(score: number): number {
  const segmentFill = (score / MAX_SCORE) * NUM_SEGMENTS
  const full = Math.floor(segmentFill)
  const partial = segmentFill - full

  if (full >= NUM_SEGMENTS) return 0
  if (full === 0 && partial === 0) return 180

  const thisAngle = segmentCenterAngle(full)
  const prevAngle = full > 0 ? segmentCenterAngle(full - 1) : 180
  const leftBoundary = (prevAngle + thisAngle) / 2

  if (partial === 0) return leftBoundary

  const nextAngle = full < NUM_SEGMENTS - 1 ? segmentCenterAngle(full + 1) : 0
  const rightBoundary = (thisAngle + nextAngle) / 2
  return leftBoundary - partial * (leftBoundary - rightBoundary)
}

function buildSweepPath(clipAngleDeg: number, cx: number, cy: number, r: number): string {
  if (clipAngleDeg >= 180) return ''
  if (clipAngleDeg <= 0) {
    return `M ${cx} ${cy} L ${cx - r} ${cy} A ${r} ${r} 0 0 1 ${cx} ${cy - r} A ${r} ${r} 0 0 1 ${cx + r} ${cy} Z`
  }
  const endRad = (clipAngleDeg * Math.PI) / 180
  const endX = cx + r * Math.cos(endRad)
  const endY = cy - r * Math.sin(endRad)
  const swept = 180 - clipAngleDeg
  const largeArc = swept > 180 ? 1 : 0
  return `M ${cx} ${cy} L ${cx - r} ${cy} A ${r} ${r} 0 ${largeArc} 1 ${endX} ${endY} Z`
}
